import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Share2, Copy, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useShareKey } from '@/hooks/useShareKey';
import { toast } from 'sonner';

interface ShareKeySheetProps {
  keyId: string;
  disabled?: boolean;
}

export function ShareKeySheet({ keyId, disabled }: ShareKeySheetProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sharedQr, setSharedQr] = useState<string | null>(null);
  const [sharedExpires, setSharedExpires] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const shareKey = useShareKey();

  const handleShare = async () => {
    if (!name.trim()) {
      toast.error('Ange mottagarens namn');
      return;
    }

    try {
      const result = await shareKey.mutateAsync({
        key_id: keyId,
        recipient_name: name.trim(),
        recipient_phone: phone.trim() || undefined,
      });

      setSharedQr(result.qr_string);
      setSharedExpires(result.expires_at);
      toast.success(`Nyckel delad med ${result.recipient_name}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte dela nyckeln');
    }
  };

  const handleCopyQr = async () => {
    if (!sharedQr) return;
    await navigator.clipboard.writeText(sharedQr);
    setCopied(true);
    toast.success('QR-kod kopierad');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setName('');
      setPhone('');
      setSharedQr(null);
      setSharedExpires(null);
      setCopied(false);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="gap-1.5 text-xs"
        >
          <Share2 className="h-3.5 w-3.5" />
          Dela
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Dela nyckel</DialogTitle>
        </DialogHeader>

        {!sharedQr ? (
          <div className="space-y-4 pt-2">
            <div>
              <label htmlFor="recipient-name" className="block text-sm font-medium mb-1">
                Mottagarens namn *
              </label>
              <input
                id="recipient-name"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Namn"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="recipient-phone" className="block text-sm font-medium mb-1">
                Telefonnummer (valfritt)
              </label>
              <input
                id="recipient-phone"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+46..."
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <Button
              className="w-full"
              onClick={handleShare}
              disabled={shareKey.isPending || !name.trim()}
            >
              {shareKey.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              Dela nyckel
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="flex flex-col items-center">
              <p className="text-sm text-muted-foreground mb-3">
                Visa denna QR-kod för <strong>{name}</strong>
              </p>
              <div className="bg-white p-3 rounded-xl shadow-sm">
                <QRCodeSVG value={sharedQr} size={180} level="M" />
              </div>
              {sharedExpires && (
                <p className="text-xs text-muted-foreground mt-2">
                  Giltig till {new Date(sharedExpires).toLocaleString('sv-SE')}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCopyQr}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copied ? 'Kopierad' : 'Kopiera QR'}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleClose}
              >
                <X className="h-4 w-4" />
                Stäng
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
